import { on } from "svelte/events";

// Event-driven read of WTO's already-decided experiment state.
//
// global.js (a WTO account script, in WTO's own script editor — account-level, not part of
// this repo) dispatches a `wto-experiment-ready` CustomEvent on `document` right after
// WT.optimize.g_RunObjList gets a new entry — the array both organic bucketing (via
// WT.optimize.setup) and fullwtpreview-forced runs converge on, so this fires for both.
//
// The event lands right as the entry is added, but its own fields can lag by ~1 tick —
// findVariationName briefly returns a placeholder ({testName: "testname-not-found",
// variationName: "BASELINE"}) instead of throwing, so resolveAndCache retries briefly rather
// than trusting the first non-throwing result.

const resolvedVariants: Record<string, string | undefined> = $state({});

const RETRY_INTERVAL_MS = 50;
const RETRY_TIMEOUT_MS = 500;

function resolveAndCache(testAlias: string): void {
  const deadline = Date.now() + RETRY_TIMEOUT_MS;
  const attempt = () => {
    try {
      const result = window.WT?.helpers.findVariationName(testAlias);
      if (result?.variationName === "BASELINE" || result?.testName === "testname-not-found") {
        throw new Error("not resolved yet");
      }
      resolvedVariants[testAlias] =
        result?.variationName != null ? String(result.variationName) : undefined;
    } catch {
      if (Date.now() < deadline) {
        setTimeout(attempt, RETRY_INTERVAL_MS);
      } else {
        resolvedVariants[testAlias] = undefined;
      }
    }
  };
  attempt();
}

let stopListening: (() => void) | undefined;

function ensureListening(): void {
  if (stopListening) return;
  stopListening = on(document, "wto-experiment-ready", (event) => {
    const testAlias = (event as CustomEvent<{ testAlias?: string }>).detail?.testAlias;
    if (testAlias) resolveAndCache(testAlias);
  });
}

// testAlias is the "Project Alias" field from the WTO UI (e.g. "ta_BlackPriceforfullpriceproducts"),
// not a name you invent. Resolves to undefined until WTO registers this experiment (or never, if
// it isn't targeted here, the visitor isn't bucketed, or the test isn't live) — never throws.
export function useWtoVariant(testAlias: string): { readonly value: string | undefined } {
  ensureListening();

  // The event may have already fired before this component mounted — check once, opportunistically.
  if (!(testAlias in resolvedVariants) && window.WT?.helpers?.findVariationName) {
    resolveAndCache(testAlias);
  }

  return {
    get value() {
      return resolvedVariants[testAlias];
    },
  };
}

// Test-only: clears shared module state so each test starts fresh.
// vi.resetModules() doesn't reliably bust this module's singleton state in browser-mode Vitest.
export function __resetWtoStateForTesting(): void {
  for (const key in resolvedVariants) delete resolvedVariants[key];
  stopListening?.();
  stopListening = undefined;
}
